import { useEffect, useMemo, useState } from "react";

import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  MenuItem,
  FormControl,
  Select,
  InputAdornment,
  Chip,
  Divider,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  CircularProgress,
} from "../../components";
import { LogsService, UsuarioService } from "../../services";
import { responseErros } from "../../utils";

const logsService = new LogsService();
const usuarioService = new UsuarioService();

const MODULOS = [
  { value: "estoque", label: "Estoque" },
  { value: "financeiro", label: "Financeiro" },
  { value: "faturamento", label: "Faturamento" },
  { value: "fiscal", label: "Fiscal" },
  { value: "integracao", label: "Integração" },
];

const METODOS = [
  { value: "PUT", label: "Atualização" },
  { value: "DELETE", label: "Exclusão" },
];

const moduloCores = {
  estoque: { bg: "#e2efff", color: "#1764b0" },
  financeiro: { bg: "#e2efff", color: "#1764b0" },
  faturamento: { bg: "#e2efff", color: "#1764b0" },
  fiscal: { bg: "#e2efff", color: "#1764b0" },
  integracao: { bg: "#e2efff", color: "#1764b0" },
};

const moduloLabel = (slug) =>
  MODULOS.find((item) => item.value === slug)?.label ||
  (slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : "—");

const acaoLabel = (method) =>
  METODOS.find((item) => item.value === method)?.label || method || "—";

const moduloFromContainer = (containerName) => {
  if (!containerName) return "";
  return containerName
    .replace(/^siaf-lite-/, "")
    .replace(/-\d+$/, "")
    .toLowerCase();
};

const chaveLog = (log) =>
  `${log.containerName}|${log.timestamp}|${log.message}|${log.usuarioId}`;

const formatarMensagem = (message) => {
  if (!message) return "—";
  return message
    .replace(/[.-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (letra) => letra.toUpperCase());
};

const formatarData = (timestamp) => {
  const date = new Date(timestamp.replace(" ", "T"));

  return {
    dia: new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "short",
    })
      .format(date)
      .replace(".", ""),
    hora: new Intl.DateTimeFormat("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date),
  };
};

const formatarValor = (valor) => {
  if (valor === null || valor === undefined || valor === "") return "—";
  if (typeof valor === "object") return JSON.stringify(valor);
  return String(valor);
};

const AuditoriaView = () => {
  const [logs, setLogs] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [usuariosList, setUsuariosList] = useState([]);
  const [busca, setBusca] = useState("");
  const [periodo, setPeriodo] = useState("7");
  const [usuarioId, setUsuarioId] = useState("");
  const [method, setMethod] = useState("");
  const [modulo, setModulo] = useState("");
  const [ordenacao, setOrdenacao] = useState("desc");
  const [expandidos, setExpandidos] = useState([]);
  const [detalhesPorLog, setDetalhesPorLog] = useState({});
  const [carregandoDetalhe, setCarregandoDetalhe] = useState({});

  useEffect(() => {
    buscarLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo, usuarioId, method, modulo]);

  const buscarLogs = () => {
    setCarregando(true);

    const hoje = new Date();
    const filtro = {};

    if (periodo !== "todos") {
      const inicio = new Date();
      inicio.setDate(hoje.getDate() - Number(periodo));
      filtro.dataInicio = inicio.toISOString().slice(0, 10);
      filtro.dataFim = hoje.toISOString().slice(0, 10);
    }
    if (usuarioId) filtro.usuarioId = usuarioId;
    if (method) filtro.method = method;
    if (modulo) filtro.modulo = modulo;

    logsService
      .getAll(filtro)
      .then((result) => {
        if (!result.isAxiosError) {
          const lista = result.data || [];
          setLogs(lista);
          buscarRelacionamentosLogs(lista);
        } else {
          responseErros(result);
          setLogs([]);
        }
      })
      .finally(() => setCarregando(false));
  };

  const buscarRelacionamentosLogs = (listaLogs) => {
    const idsUsuarios = [
      ...new Set(
        listaLogs
          .map(({ usuarioId }) => (usuarioId ? usuarioId : 0))
          .filter(
            (usuarioId, index, lista) => lista.indexOf(usuarioId) === index,
          ),
      ),
    ].filter((id) => id);

    buscarUsuarios(idsUsuarios);
  };

  const buscarUsuarios = (listaIds) => {
    if (!listaIds.length) return;

    const filtro = { id: listaIds, nonPaginated: true };
    usuarioService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setUsuariosList((atual) => {
          const mapa = new Map(atual.map((usuario) => [usuario.id, usuario]));
          (result.data || []).forEach((usuario) =>
            mapa.set(usuario.id, usuario),
          );
          return [...mapa.values()];
        });
      } else {
        responseErros(result);
      }
    });
  };

  const registros = useMemo(() => {
    const textoBusca = busca.toLowerCase().trim();
    return logs
      .map((log) => {
        const usuario = usuariosList.find(
          (usuario) => usuario.id === log.usuarioId,
        );
        return {
          ...log,
          key: chaveLog(log),
          modulo: moduloFromContainer(log.containerName),
          acao: acaoLabel(log.method),
          usuario: usuario?.nome || `Usuario #${log.usuarioId}`,
          perfilUsuario: usuario?.perfilUsuario?.nome || "",
        };
      })
      .filter((log) => {
        if (!textoBusca) return true;
        const texto = [log.message, log.modulo, log.acao, log.usuario]
          .join(" ")
          .toLowerCase();
        return texto.includes(textoBusca);
      })
      .sort((a, b) =>
        ordenacao === "desc"
          ? new Date(b.timestamp) - new Date(a.timestamp)
          : new Date(a.timestamp) - new Date(b.timestamp),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs, busca, ordenacao, usuariosList]);

  const alternarExpandido = (log) => {
    const jaExpandido = expandidos.includes(log.key);

    setExpandidos((atuais) =>
      jaExpandido
        ? atuais.filter((item) => item !== log.key)
        : [...atuais, log.key],
    );

    if (!jaExpandido && !detalhesPorLog[log.key]) {
      setCarregandoDetalhe((atual) => ({ ...atual, [log.key]: true }));
      logsService
        .getDetalhe({
          timestamp: log.timestamp,
          message: log.message,
          containerName: log.containerName,
          usuarioId: log.usuarioId,
        })
        .then((result) => {
          if (!result.isAxiosError) {
            setDetalhesPorLog((atual) => ({
              ...atual,
              [log.key]: result.data,
            }));
          } else {
            responseErros(result);
          }
        })
        .finally(() =>
          setCarregandoDetalhe((atual) => ({ ...atual, [log.key]: false })),
        );
    }
  };

  const totalUsuarios = new Set(registros.map((item) => item.usuarioId)).size;
  const totalCriacoes = registros.filter(
    (item) => item.method === "POST",
  ).length;

  const selectSx = {
    bgcolor: "#f5f7fa",
    borderRadius: 2,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d5dce5",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#9baec4",
    },
  };

  const labelSx = {
    display: "block",
    color: "#60758d",
    mb: 0.65,
  };

  const StatCard = ({ valor, titulo, descricao, cor }) => (
    <Box sx={{ px: 1.75, py: 1.5, minWidth: 0 }}>
      <Typography
        className="fw-bold"
        sx={{
          lineHeight: 1.2,
          color: cor || "#12243a",
        }}
      >
        {valor}
      </Typography>
      <Typography>{titulo}</Typography>
      <Typography variant="body2" className=" text-muted">
        {descricao}
      </Typography>
    </Box>
  );

  return (
    <Box
      className="main-layout-container"
      sx={{
        p: { xs: 1.5, md: 2 },
        color: "#12243a",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "flex-end" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#1680ee",
              textTransform: "uppercase",
            }}
          >
            Auditoria
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            Log do sistema
          </Typography>

          <Typography variant="body2" sx={{ color: "#71849b" }}>
            Acompanhe todas as ações realizadas nos módulos do sistema.
          </Typography>
        </Box>

        <Stack direction="row" alignItems="center" spacing={0.75}>
          <i className="ph-fill ph-check-circle" style={{ color: "#16a36a" }} />

          <Typography variant="caption" sx={{ color: "#71849b" }}>
            Atualizado hoje às 10:49
          </Typography>
        </Stack>
      </Stack>

      <Paper
        className="rounded-3"
        variant="outlined"
        sx={{
          borderColor: "#d4dce5",
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        <Grid container>
          <Grid item xs={12} sm={4}>
            <StatCard
              valor={registros.length}
              titulo="Registros no período"
              descricao={`${registros.length} encontrados nesta consulta`}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                borderLeft: { sm: "1px solid #d4dce5" },
                borderRight: { sm: "1px solid #d4dce5" },
              }}
            >
              <StatCard
                valor={totalUsuarios}
                titulo="Usuários ativos"
                descricao="Usuários com ações registradas"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <StatCard
              valor={totalCriacoes}
              titulo="Criações"
              descricao="Registros criados no período"
              cor="#1a9b5c"
            />
          </Grid>
        </Grid>

        <Divider />

        <Box sx={{ p: 1.75 }}>
          <TextField
            fullWidth
            placeholder="Buscar por ação, usuário, registro, campo ou valor..."
            value={busca}
            className="mb-3"
            onChange={(event) => setBusca(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <i className="ph ph-magnifying-glass" color="#73879f" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#f5f7fa",
                borderRadius: 1,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d5dce5",
              },
            }}
          />

          <Grid container spacing={1.2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography sx={labelSx}>Período</Typography>

              <FormControl fullWidth>
                <Select
                  value={periodo}
                  onChange={(e) => setPeriodo(e.target.value)}
                  disableClearable
                  sx={selectSx}
                  startAdornment={
                    <InputAdornment position="start">
                      <i
                        className="ph ph-calendar-blank"
                        size={14}
                        color="#60758d"
                      />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="7">7 dias</MenuItem>
                  <MenuItem value="30">30 dias</MenuItem>
                  <MenuItem value="90">90 dias</MenuItem>
                  <MenuItem value="todos">Todo o período</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Typography sx={labelSx}>Usuário</Typography>

              <FormControl fullWidth>
                <Select
                  value={usuarioId}
                  onChange={(e) => setUsuarioId(e.target.value)}
                  disableClearable
                  sx={selectSx}
                >
                  <MenuItem value="">Todos</MenuItem>

                  {usuariosList.map((usuario) => (
                    <MenuItem key={usuario.id} value={usuario.id}>
                      {usuario.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Typography sx={labelSx}>Tipo de ação</Typography>

              <FormControl fullWidth>
                <Select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  disableClearable
                  sx={selectSx}
                >
                  <MenuItem value="">Todas</MenuItem>

                  {METODOS.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Typography sx={labelSx}>Módulo</Typography>

              <FormControl fullWidth>
                <Select
                  value={modulo}
                  onChange={(e) => setModulo(e.target.value)}
                  disableClearable
                  sx={selectSx}
                >
                  <MenuItem value="">Todos</MenuItem>

                  {MODULOS.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.2 }}
      >
        <Typography>
          {registros.length}{" "}
          {registros.length === 1
            ? "registro encontrado"
            : "registros encontrados"}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <i className="ph ph-clock" size={12} color="#8294a9" />

          <TextField
            select
            size="small"
            value={ordenacao}
            onChange={(e) => setOrdenacao(e.target.value)}
            variant="standard"
            slotProps={{
              select: {
                disableUnderline: true,
                sx: { fontSize: 9, color: "#71849b" },
              },
            }}
          >
            <MenuItem value="desc">Mais recentes primeiro</MenuItem>
            <MenuItem value="asc">Mais antigos primeiro</MenuItem>
          </TextField>
        </Stack>
      </Stack>

      <Stack spacing={1}>
        {registros.map((item) => {
          const expandido = expandidos.includes(item.key);
          const data = formatarData(item.timestamp);
          const cores = moduloCores[item.modulo] || {
            bg: "#e2efff",
            color: "#1764b0",
          };
          const detalhe = detalhesPorLog[item.key];
          const detalhesCampos = (detalhe?.diferencas || []).map(
            (diferenca) => ({
              campo: diferenca.campo,
              anterior: formatarValor(diferenca.anterior),
              novo: formatarValor(diferenca.novo),
            }),
          );

          return (
            <Paper
              key={item.key}
              variant="outlined"
              sx={{
                borderRadius: 1,
                overflow: "hidden",
                borderColor: "#d4dce5",
                bgcolor: "#fff",
                boxShadow: "0 1px 2px rgba(15, 35, 55, 0.06)",
                transition: "border-color 0.2s",
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={expandido}
                onClick={() => alternarExpandido(item)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    alternarExpandido(item);
                  }
                }}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "5rem 24rem minmax(0, 1fr) 3rem",
                    md: "8rem 40rem minmax(0, 1fr) 3rem",
                    lg: "8rem 40rem minmax(0, 1fr) 3rem",
                  },
                  gap: 2,
                  alignItems: "center",
                  px: 1.5,
                  py: 2,
                  cursor: "pointer",
                  minHeight: 64,
                  "&:hover": {
                    bgcolor: "rgba(30, 70, 110, 0.025)",
                  },
                  "&:focus-visible": {
                    outline: "2px solid #1680ee",
                    outlineOffset: -2,
                  },
                }}
              >
                <Box>
                  <Typography variant="body2" gutterBottom>
                    {data.dia}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#71849b" }}>
                    {data.hora}
                  </Typography>
                </Box>

                <Box
                  className=""
                  sx={{
                    minWidth: 0,
                    gap: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                  >
                    <Chip
                      label={moduloLabel(item.modulo)}
                      size="small"
                      sx={{
                        borderRadius: 0.5,
                        bgcolor: cores.bg,
                        color: cores.color,
                        fontWeight: 600,
                        "& .MuiChip-label": { px: 0.8 },
                      }}
                    />

                    <Typography variant="body2" sx={{ color: "#71849b" }}>
                      {item.acao}
                    </Typography>
                  </Stack>

                  <Typography className="fw-bold">
                    {formatarMensagem(item.message)}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#71849b",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.containerName}
                  </Typography>
                </Box>

                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <Typography>{item.usuario}</Typography>
                  <Typography sx={{ color: "#71849b" }}>
                    {item.perfilUsuario}
                  </Typography>
                </Box>

                <Tooltip
                  title={expandido ? "Recolher detalhes" : "Ver detalhes"}
                >
                  <IconButton
                    size="small"
                    aria-label={
                      expandido ? "Recolher detalhes" : "Ver detalhes"
                    }
                    tabIndex={-1}
                    sx={{ color: "#8294a9" }}
                  >
                    {expandido ? (
                      <i className="ph ph-caret-down" />
                    ) : (
                      <i className="ph ph-caret-right" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              <Collapse in={expandido} timeout="auto" unmountOnExit>
                <Divider />

                <Box
                  sx={{
                    bgcolor: "#f8f9fa",
                    px: { xs: 1.5, md: 12.25 },
                    py: 1.5,
                  }}
                >
                  <Stack
                    direction="row"
                    useFlexGap
                    flexWrap="wrap"
                    columnGap={2}
                    rowGap={0.75}
                    className="mb-4"
                  >
                    <Typography sx={{ color: "#526981" }}>
                      <Box
                        component="span"
                        className="fw-bold"
                        sx={{ color: "#142a43" }}
                      >
                        Registro:
                      </Box>{" "}
                      {item.message}
                    </Typography>

                    <Typography sx={{ color: "#526981" }}>
                      <Box
                        component="span"
                        className="fw-bold"
                        sx={{ color: "#142a43" }}
                      >
                        Responsável:
                      </Box>{" "}
                      {item.usuario}
                    </Typography>

                    <Typography sx={{ color: "#526981" }}>
                      <Box
                        component="span"
                        className="fw-bold"
                        sx={{ color: "#142a43" }}
                      >
                        Serviço:
                      </Box>{" "}
                      Manual
                    </Typography>
                  </Stack>

                  {carregandoDetalhe[item.key] && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" sx={{ color: "#71849b" }}>
                        Carregando detalhes...
                      </Typography>
                    </Stack>
                  )}

                  {!carregandoDetalhe[item.key] &&
                    detalhesCampos.length > 0 && (
                      <Stack spacing={1.2}>
                        {detalhesCampos.map((campo, index) => (
                          <Box key={`${item.key}-${index}`}>
                            <Typography
                              variant="body2"
                              className="text-uppercase mb-1"
                              sx={{
                                color: "#60758d",
                              }}
                            >
                              {campo.campo}
                            </Typography>

                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={12} md={5.5}>
                                <Box
                                  className="p-2 rounded-2"
                                  sx={{
                                    border: "1px solid #ffc2c2",
                                    bgcolor: "#fff",
                                    minHeight: 41,
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    className="text-uppercase mb-1"
                                    sx={{
                                      color: "#d94848",
                                    }}
                                  >
                                    ANTERIOR
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#8f5360",
                                      textDecoration:
                                        campo.anterior === "—"
                                          ? "none"
                                          : "line-through",
                                    }}
                                  >
                                    {campo.anterior}
                                  </Typography>
                                </Box>
                              </Grid>

                              <Grid
                                item
                                xs={12}
                                md={1}
                                sx={{
                                  display: {
                                    xs: "none",
                                    md: "flex",
                                  },
                                  justifyContent: "center",
                                }}
                              >
                                <i
                                  className="ph ph-arrow-right"
                                  style={{ fontSize: 15 }}
                                />
                              </Grid>

                              <Grid item xs={12} md={5.5}>
                                <Box
                                  className="p-2 rounded-2"
                                  sx={{
                                    border: "1px solid #8ee0ad",
                                    bgcolor: "#e0f8e9",
                                    minHeight: 41,
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    className="text-uppercase mb-1"
                                    sx={{
                                      color: "#13834a",
                                    }}
                                  >
                                    NOVO
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#123b29" }}
                                  >
                                    {campo.novo}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                      </Stack>
                    )}

                  {!carregandoDetalhe[item.key] &&
                    detalhe &&
                    detalhesCampos.length === 0 && (
                      <Typography variant="body2" sx={{ color: "#71849b" }}>
                        Nenhuma alteração de campo identificada.
                      </Typography>
                    )}

                  {!carregandoDetalhe[item.key] && !detalhe && (
                    <Typography variant="body2" sx={{ color: "#71849b" }}>
                      Detalhe não disponível para este registro.
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Paper>
          );
        })}

        {!carregando && registros.length === 0 && (
          <Paper
            className="p-5 text-center"
            variant="outlined"
            sx={{
              borderColor: "#d4dce5",
              boxShadow: "none",
            }}
          >
            <i className="ph ph-clock-counter-clockwise" />

            <Typography className="mt-1">Nenhum registro encontrado</Typography>

            <Typography variant="body2" className="text-muted">
              Altere os filtros ou tente buscar por outro termo.
            </Typography>
          </Paper>
        )}

        {carregando && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default AuditoriaView;
